import InlineSpinner from '../../../Atoms/InlineSpinner'
import Svg from '../../../Atoms/Svg'
import TextLink from '../../../Atoms/TextLink'
import Popover from '../../../Components/Popover'
import { ListContext } from '../../../Contexts/ListContext/ListContext'
import { NotificationContext } from '../../../Contexts/NotificationContext/NotificationContext'
import Button from '../../../Molecules/Button'
import styles from './BuildListActions.module.scss'

import React, { useCallback, useContext, useEffect, useState } from 'react'

import PropTypes from 'prop-types'

import Event from 'Utilities/Event'

const BuildListActions = ({ product }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isRemoveItemLoading, setIsRemoveItemLoading] = useState(false)

  const { PushNotification } = useContext(NotificationContext)
  const {
    listContext,
    getUserLists,
    moveItemBetweenLists,
    moveArticleBetweenLists,
    removeItemFromList,
    removeArticleFromList,
    showListDrawer,
    hideListDrawer,
  } = useContext(ListContext)

  const { selectedListId, lists } = listContext

  const isProducts = !!product?.skuBaseNumber

  useEffect(() => {
    if (!lists) {
      getUserLists()
    }
  }, [lists])

  const handleClick = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setIsOpen(!isOpen)
  }

  const sendMisoInteraction = () => {
    if (window?.fireMisoInteraction) {
      let productId
      let sku = null
      if (isProducts) {
        productId = product.skuBaseNumber
        sku = product.sku
      } else {
        productId = product.id
      }
      const isArticle = !isProducts
      window.fireMisoInteraction(
        {
          prod_id: productId,
          sku,
          eventType: 'removeFromList',
        },
        isArticle,
      )
    }
  }

  const displayNotification = (isSuccess, successMessage, errorMessage) => {
    if (isSuccess)
      PushNotification(successMessage, null, 5000, null, {
        icon: 'check',
        intent: 'positive',
      })
    else
      PushNotification(errorMessage, null, 5000, null, {
        icon: 'unavailable',
        intent: 'negative',
      })
  }

  const removeItem = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsRemoveItemLoading(true)
    sendMisoInteraction()

    let isSuccess

    try {
      if (isProducts) {
        isSuccess = await removeItemFromList(selectedListId, product.listProductId)
      } else {
        isSuccess = await removeArticleFromList(selectedListId, product.id)
      }

      displayNotification(isSuccess, 'Item Removed', 'Item not removed, please try again')

      Event({
        category: 'BuildList',
        action: 'Removed item from list',
        label: isProducts ? 'Product' : 'Article',
      })()

      if (isSuccess) {
        setIsOpen(false)
        await getUserLists(true)
      }
    } catch (error) {
      console.error('Error removing item:', error)
      displayNotification(false, 'Item Removed', 'Item not removed due to an error. Please try again.')
    }

    setIsRemoveItemLoading(false)
  }

  const handleMoveToList = useCallback(
    async (e) => {
      e.preventDefault()
      e.stopPropagation()
      try {
        showListDrawer(null, [product], 'product', true)
        setIsOpen(false)
      } catch (error) {
        console.error('Error moving item to list:', error)
        displayNotification(false, 'Move to List Failed', 'Unable to move the item to the list. Please try again.')
      }
    },
    [product, showListDrawer],
  )

  return (
    <div className={styles.wrapper} data-testid={`buildList_kebab_${product?.skuBaseNumber}`}>
      <Popover
        isOpen={isOpen}
        onToggleMenu={() => setIsOpen(!isOpen)}
        content={
          <div className={styles.linkContainer}>
            <div className={styles.linkItem} data-testid={`buildList_delete_${product?.skuBaseNumber}`}>
              {isRemoveItemLoading ? (
                <InlineSpinner intent={'action'} size={0.5} />
              ) : (
                <TextLink tone="contrast" size={0.875} href="#" onClick={removeItem}>
                  Delete
                </TextLink>
              )}
            </div>
            <hr className={styles.separator} />
            <div className={styles.linkItem} data-testid={`buildList_moveToList_${product?.skuBaseNumber}`}>
              <TextLink tone="contrast" size={0.875} href="#" onClick={handleMoveToList}>
                Move to List
              </TextLink>
            </div>
          </div>
        }
      >
        <Button
          onClick={handleClick}
          className={styles.buttonSmall}
          intent="facade"
          icon="kebab"
          segmentEvent={{
            event: 'Clicked',
            properties: { element: 'BuildListActions-Kebab' },
          }}
          size="small"
          inline
        >
          <Svg brand="" icon="kebab" size={1.25} tone="subtle" />
        </Button>
      </Popover>
    </div>
  )
}

BuildListActions.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number.isRequired,
    skuBaseNumber: PropTypes.string,
    sku: PropTypes.string,
    listProductId: PropTypes.number,
  }).isRequired,
}

export default BuildListActions
